import { map } from 'rxjs/operators';
import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { Group } from 'src/app/models/group.model';
import { GroupMember } from 'src/app/models/groupMember.model';
import { Member } from '../portal/portal.service';
import { AppUser } from 'src/app/models/appUser.model';
import { PortalMember } from 'src/app/models/portalMember.model';
import { AppDevice } from 'src/app/models/appDevice.model';
import { AppSettingsService } from '../app-settings/app-settings.service';

export class GroupAndMembers {
  constructor(newGroup: Group, newMembers: Member[]) {
    this.group = newGroup;
    this.members = newMembers;
  }

  group: Group;
  members: Member[];
}

@Injectable({
  providedIn: 'root'
})
export class GroupService {

  constructor(
    private http: HttpClient,
    private settings: AppSettingsService,
  ) { }

  getGroupByID(portalID: number, groupID: number): Observable<Group> {
    const url = this.settings.apiUrl + `portals/${portalID}/groups/${groupID}`;

    return this.http.get(url).pipe(
      map((data: any) => {
        return new Group(data);
      })
    );
  }

  getGroupsByUser(): Observable<Group[]> {
    const url = this.settings.apiUrl + `groups`;

    return this.http.get(url).pipe(
      map((data: any) => {
        let groups: Group[] = [];
        data.forEach(group => {
          groups.push(new Group(group));
        });
        return groups;
      })
    );
  }

  getUserGroupsByPortalID(portalID: number, userGroups: boolean): Observable<Group[]> {
    const params = {
      portal_id: portalID,
      user_groups: userGroups
    }

    const url = this.settings.apiUrl + `groups?parameters=` + JSON.stringify(params);

    return this.http.get(url).pipe(
      map((data: any) => {
        let groups: Group[] = [];
        data.forEach(group => {
          groups.push(new Group(group));
        });
        return groups;
      })
    );
  }

  getDeletedGroupsByPortalID(portalID: number) {
    const params = {
      portal_id: portalID,
      is_deleted: true
    }

    const url = this.settings.apiUrl + `groups?parameters=` + JSON.stringify(params);

    return this.http.get(url).pipe(
      map((data: any) => {
        let groups: Group[] = [];
        data.forEach(group => {
          groups.push(new Group(group));
        });
        return groups;
      })
    );
  }

  createGroup(portalID: number, group: Group): Observable<Group> {
    const body = {
      group_name: group.groupName,
      group_description: group.groupDescription,
    }

    const url = this.settings.apiUrl + `portals/${portalID}/groups`;

    return this.http.post(url, body).pipe(
      map((data: Group) => {
        return new Group(data);
      })
    );
  }

  updateGroup(portalID: number, group: Group): Observable<Group> {
    const body = {
      group_name: group.groupName,
      group_description: group.groupDescription,
    }

    const url = this.settings.apiUrl + `portals/${portalID}/groups/${group.id}`;

    return this.http.put(url, body).pipe(
      map((data: Group) => {
        return new Group(data);
      })
    );
  }

  deleteGroup(portalID: number, groupID: number) {
    const url = this.settings.apiUrl + `portals/${portalID}/groups/${groupID}`

    return this.http.delete(url, {}).pipe();
  }

  restoreGroup(portalID: number, groupID: number) {
    const url = this.settings.apiUrl + `portals/${portalID}/groups/${groupID}/restore`

    return this.http.put(url, {}).pipe();
  }

  getGroupMembers(portalID: number, groupID: number): Observable<GroupMember[]> {
    const url = this.settings.apiUrl + `portals/${portalID}/groups/${groupID}/members`;

    return this.http.get(url).pipe(
      map((data: any) => {
        let groupMembers: GroupMember[] = [];
        if(data) {
          data.forEach(groupMember => {
            groupMembers.push(new GroupMember(groupMember));
          });
        }
        return groupMembers;
      })
    );
  }

  getGroupsAndMembers(portalID: number): Observable<GroupAndMembers[]> {
    const url = this.settings.apiUrl + `portals/${portalID}/groups`;

    return this.http.get(url).pipe(
      map((data: any) => {
        let groupsAndMembers: GroupAndMembers[] = [];
        if(data) {
          data.forEach((g: any) => {
            let group: Group = new Group(g.group);
            let members: Member[] = [];
            if (g.members != null) {
              g.members.forEach((member: any) => {
                let mAppUser: AppUser = new AppUser(member.app_user);
                let mPortalMember: PortalMember = new PortalMember(member.portal_member);
                let mAppDevice: AppDevice = new AppDevice(member.last_logged_in_app_device);
                members.push(new Member(mAppUser, mPortalMember, mAppDevice));
              });
            }
            groupsAndMembers.push(new GroupAndMembers(group, members));
          });
        }
        return groupsAndMembers;
      })
    );
  }

  addUsersToGroup(portalID: number, groupID: number, userIds: number[]): Observable<GroupMember[]> {
    const body = {
      user_ids: userIds
    }

    const url = this.settings.apiUrl + `portals/${portalID}/groups/${groupID}/members`

    return this.http.post(url, body).pipe(
      map((data: any) => {
        let groupMembers: GroupMember[] = [];
        if(data && data.length > 0) {
          data.forEach(groupMember => {
            groupMembers.push(new GroupMember(groupMember));
          });
        }
        return groupMembers;
      })
    );
  }

  removeUsersFromGroup(portalID: number, groupID: number, userIDs: number[]): Observable<GroupMember> {
    const httpOptions = {
      body: {
        user_ids: userIDs
      }
    }

    const url = this.settings.apiUrl + `portals/${portalID}/groups/${groupID}/members`

    return this.http.delete(url, httpOptions).pipe(
      map((data: any) => {
        return new GroupMember(data);
      })
    );
  }
  
  getGroupMembersCSVExport(portalID: number, groupID: number) {
    const url = this.settings.apiUrl + `portals/${portalID}/groups/${groupID}/csv`;

    return this.http.get(url).pipe(
      map((data: any) => {
        const exportedEvents: string = data.exported_members;
        return exportedEvents;
      })
    );
  }
}
